/**
 * B1053 — Page « Sentinelle connexion » (Eric 2026-09-04 SPEC STRICK PROD,
 * libellé corrigé en B1110).
 *
 * Créée après l'incident 2026-09-02/04 : la connexion à Knoria était morte
 * pendant deux jours et `/api/health` répondait 200 en continu. Le monitoring
 * vérifiait que le serveur vivait — pas qu'un humain pouvait entrer.
 *
 * Cette page montre, à la minute :
 *   • le verdict global (vert / jaune / rouge / inconnu si la sonde est figée)
 *   • le détail des 7 étapes du dernier parcours de connexion
 *   • l'historique des passages avec courbe de latence
 *   • la répartition des échecs par étape (diagnostic immédiat)
 *   • les rapports du canari navigateur (pannes de transport axios, cf. B1052)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { ArrowClockwise, Play, Heartbeat, Envelope, Gear } from "@phosphor-icons/react";
import { LatencyTrendChart } from "../../components/admin/LatencyTrendChart";
import { MonitoringCoveragePanel } from "../../components/admin/MonitoringCoveragePanel";
import ReportSendLog from "../../components/admin/ReportSendLog";
import ReportPreviewModal from "../../components/admin/ReportPreviewModal";
import PipelineDurationsPanel from "../../components/admin/PipelineDurationsPanel";
import RouteLatencyPanel from "../../components/admin/RouteLatencyPanel"; // B1088 — Tableau de latence
import SystemNotificationsCard from "../../components/admin/SystemNotificationsCard"; // Tâche 1143
import AIKeysSentinelCard from "../../components/admin/AIKeysSentinelCard"; // Tâche 1207
import GatewayErrorsPanel from "../../components/admin/GatewayErrorsPanel"; // Bogue 1202
import MasterKeyHealthCard from "../../components/admin/MasterKeyHealthCard"; // Lot F
import KillSwitchesCard from "../../components/admin/KillSwitchesCard"; // Lot D
import AiTitlesSentinelCard from "../../components/admin/AiTitlesSentinelCard"; // Tache 1291
import DiskSentinelCard from "../../components/admin/DiskSentinelCard"; // Tâche 1216
import EmbeddingScanCard from "../../components/admin/EmbeddingScanCard"; // Tâche 1246
import DailyJobsCard from "../../components/admin/DailyJobsCard"; // Tâches 1247/1248
import ResendSentinelCard from "../../components/admin/ResendSentinelCard"; // Tâche 1250
import StripeSentinelCard from "../../components/admin/StripeSentinelCard"; // Tâche 1257
import LegacyStreamTokenCard from "../../components/admin/LegacyStreamTokenCard"; // Tâche 1224
import TestEmailGateCard from "../../components/admin/TestEmailGateCard"; // Tâche 1227
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_MS = 60_000;

// Tâche 1363 — Les deux langues sur la MÊME ligne : une table jumelle
// divergerait au premier état ajouté.
const OVERALL = {
  green: { label: "La connexion fonctionne", label_en: "Login is working", dot: "bg-emerald-500", box: "bg-emerald-50 text-emerald-900 border-emerald-300" },
  yellow: { label: "Échecs intermittents", label_en: "Intermittent failures", dot: "bg-amber-500", box: "bg-amber-50 text-amber-900 border-amber-300" },
  red: { label: "CONNEXION IMPOSSIBLE", label_en: "LOGIN IMPOSSIBLE", dot: "bg-red-500", box: "bg-red-50 text-red-900 border-red-400" },
  unknown: { label: "État inconnu — sonde figée", label_en: "Unknown state — probe stalled", dot: "bg-zinc-400", box: "bg-zinc-100 text-zinc-800 border-zinc-300" },
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return String(iso);
  }
};

const fmtAgo = (seconds) => {
  if (seconds == null) return "jamais";
  if (seconds < 60) return `il y a ${seconds} s`;
  if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)} min`;
  return `il y a ${Math.floor(seconds / 3600)} h`;
};

const fmtShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

function LatencySparkline({ runs }) {
  const { tr } = useLang();
  const series = [...runs].reverse().slice(-60);
  if (series.length < 2) return null;
  const max = Math.max(...series.map((r) => r.total_ms || 0), 1);
  return (
    <div className="flex items-end gap-[3px] h-24" data-testid="sentinel-latency-chart">
      {series.map((r, i) => (
        <div
          key={r.id || i}
          title={`${fmtTime(r.started_at)} — ${r.total_ms} ms${r.skipped ? tr(" (ignoré : rate-limiter)", " (skipped: rate limiter)") : r.ok ? "" : tr(" (échec)", " (failed)")}`}
          className={`w-2.5 rounded-t transition-all ${r.skipped ? "bg-zinc-300" : r.ok ? "bg-[#002FA7]/70 hover:bg-[#002FA7]" : "bg-red-500"}`}
          style={{ height: `${Math.max(4, ((r.total_ms || 0) / max) * 100)}%` }}
        />
      ))}
    </div>
  );
}

export default function LoginSentinelPage() {
  // Tâches 1362 · 1363 (Eric 2026-06-30) — Page entièrement française, accents
  // compris, y compris en version anglaise.
  const { tr, lang } = useLang();
  const [data, setData] = useState(null);
  const [canaries, setCanaries] = useState([]);
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [trend, setTrend] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const timerRef = useRef(null);

  // Tâche 1249 (Eric 2026-06-28) — Un seul appel pour toutes les cartes.
  // La page déclenchait 23 appels API par affichage (seuil 15) et l'incident
  // « page gourmande » restait ouvert : chaque carte faisait le sien. Les
  // données arrivent maintenant groupées et descendent en propriétés ; chaque
  // carte garde son bouton « Rafraîchir » pour son propre appel.
  const [bootstrap, setBootstrap] = useState(null);
  const [bootstrapPending, setBootstrapPending] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Tâche 1249 — UN seul appel : la page prenait 27 appels distincts par
      // affichage (seuil de la surveillance : 15). Les agrégats arrivent
      // groupés ; le repli sur les appels séparés n'existe plus que dans les
      // cartes, via leur bouton « Rafraîchir ».
      const bundle = await axios
        .get(`${API}/api/sentinel/bootstrap`, { withCredentials: true })
        .then((r) => r.data)
        .catch(() => null);
      const pick = (v) => (v && !v.error ? v : null);
      // Repli : si l'appel groupé tombe, la sentinelle de connexion — le cœur
      // de la page — se recharge seule. Sans ce repli, une panne du groupage
      // afficherait « sonde figée » alors que la sonde va très bien.
      const status = pick(bundle?.login)
        ? { data: bundle.login }
        : await axios
            .get(`${API}/api/sentinel/login/status?limit=120`, { withCredentials: true })
            .catch(() => ({ data: {} }));
      const canary = { data: pick(bundle?.transport_canary) || { reports: [] } };
      const health = { data: pick(bundle?.health_status) };
      const trendRes = { data: pick(bundle?.latency_trend) };
      const coverageRes = { data: pick(bundle?.coverage) };
      setBootstrap(bundle || null);
      setBootstrapPending(false);
      setData(status.data);
      setCanaries(canary.data?.reports || []);
      setMonitor(health.data);
      setTrend(trendRes.data);
      setCoverage(coverageRes.data);
      const conf = await axios
        .get(`${API}/api/health-monitor/settings`, { withCredentials: true })
        .catch(() => ({ data: null }));
      if (conf.data) setSettings(conf.data);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setBootstrapPending(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, AUTO_REFRESH_MS);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [load]);

  const probeNow = async () => {
    setProbing(true);
    try {
      const { data: run } = await axios.post(
        `${API}/api/sentinel/login/run-now`, {}, { withCredentials: true }
      );
      if (run.ok) toast.success(`Parcours de connexion réussi en ${run.total_ms} ms`);
      else toast.error(`Échec à l'étape « ${run.failed_step} » — ${run.error || ""}`);
      await load();
    } catch (e) {
      toast.error("Sonde impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setProbing(false);
    }
  };

  const sendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      const { data: res } = await axios.post(
        `${API}/api/reliability/weekly-report/send`, {}, { withCredentials: true }
      );
      if (res.sent) toast.success(`Rapport envoyé à ${res.recipients.join(", ")}`);
      else toast.info(tr("Rapport généré mais non envoyé (alertes désactivées en preview)", "Report generated but not sent (alerts disabled in preview)"));
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setSendingReport(false);
    }
  };

  // B1057 / B1058 / B1060 — Enregistre uniquement ce qui a changé : l'API
  // n'applique que les champs présents.
  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const { data: res } = await axios.patch(
        `${API}/api/health-monitor/settings`, patch, { withCredentials: true }
      );
      setSettings(res);
      toast.success(tr("Réglages enregistrés", "Settings saved"));
      await load();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingSettings(false);
    }
  };

  // B1111 (Eric 2026-09-08) — Suppression d'une adresse ajoutée à la main.
  // Le serveur refuse (409) un fondateur ou un admin technique : le message
  // rappelle alors qu'il faut décocher, pas supprimer.
  const removeRecipient = async (email) => {
    const ok = await confirmDialog(
      `Retirer définitivement ${email} de la liste des destinataires ?`
    );
    if (!ok) return;
    setSavingSettings(true);
    try {
      const { data: res } = await axios.delete(
        `${API}/api/health-monitor/settings/recipients/${encodeURIComponent(email)}`,
        { withCredentials: true }
      );
      setSettings(res);
      toast.success(`${email} retiré de la liste`);
      await load();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingSettings(false);
    }
  };


  // B1062 — Force un tour d'audit (pipeline, clés BYOK, base, stockage,
  // saturation navigateur) sans attendre le cycle de 5 minutes.
  const runAudit = async () => {
    setAuditing(true);
    try {
      await axios.post(`${API}/api/health-monitor/run-audit`, {}, { withCredentials: true });
      toast.success(tr("Audit exécuté — le tableau reflète l'état réel", "Audit run — the table reflects the real state"));
      await load();
    } catch (e) {
      toast.error(tr("Audit impossible : ", "Audit failed: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setAuditing(false);
    }
  };

  const toggleRecipient = (email) => {
    const current = (settings?.candidates || []).filter((c) => c.selected).map((c) => c.email);
    const next = current.includes(email)
      ? current.filter((e) => e !== email)
      : [...current, email];
    if (next.length === 0) {
      toast.error(tr("Garde au moins un destinataire : une alerte qui ne part chez personne ne sert à rien", "Keep at least one recipient: an alert that reaches nobody is useless"));
      return;
    }
    saveSettings({ alert_recipients: next });
  };

  const overall = OVERALL[data?.overall || "unknown"];  const lastRun = data?.runs?.[0];
  const stats = data?.stats || {};

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="login-sentinel-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <Heartbeat size={26} weight="fill" className="text-[#002FA7]" />
            Sentinelle connexion
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            {tr(
              `Un vrai parcours de connexion (jeton CSRF, mot de passe, profil, rotation de jeton, déconnexion) est exécuté toutes les ${Math.round((data?.interval_seconds || 300) / 60)} minutes avec un compte dédié à accès minimal. Alerte courriel au premier échec, puis rappel horaire.`,
              `A real login journey (CSRF token, password, profile, token rotation, sign-out) runs every ${Math.round((data?.interval_seconds || 300) / 60)} minutes with a dedicated least-privilege account. Email alert on the first failure, then an hourly reminder.`,
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={probeNow}
            disabled={probing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="sentinel-probe-now-btn"
          >
            <Play size={16} weight="fill" />
            {probing ? "Sonde en cours…" : "Sonder maintenant"}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="sentinel-refresh-btn"
          >
            <ArrowClockwise size={16} />
            {tr("Rafraîchir", "Refresh")}
          </button>
        </div>
      </header>

      <div className={`rounded-xl border p-5 ${overall.box}`} data-testid="sentinel-overall">
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${overall.dot}`} />
          <span className="text-lg font-bold">{(lang === "en" ? overall.label_en : overall.label) || overall.label}</span>
          {data?.consecutive_failures > 0 && (
            <span className="text-sm font-semibold" data-testid="sentinel-consecutive">
              · {data.consecutive_failures} échec(s) consécutif(s)
            </span>
          )}
        </div>
        {data?.is_stale && (
          <p className="text-sm mt-2" data-testid="sentinel-stale-warning">
            {tr(
              `Aucun passage depuis plus de ${Math.round((data.stale_after_seconds || 900) / 60)} minutes : la sentinelle elle-même est peut-être bloquée. L'état réel de la connexion est inconnu.`,
              `No run for over ${Math.round((data.stale_after_seconds || 900) / 60)} minutes: the sentinel itself may be stuck. The real state of the login is unknown.`,
            )}
          </p>
        )}
        {data?.last_error && !data?.ok && (
          <p className="text-sm mt-2 font-mono" data-testid="sentinel-last-error">{data.last_error}</p>
        )}
        {data?.configured === false && (
          <p className="text-sm mt-2" data-testid="sentinel-not-configured">
            {tr("La variable d'environnement", "The environment variable")} <code>SENTINEL_PASSWORD</code> {tr("est absente — la sonde ne peut pas s'exécuter.", "is missing — the probe cannot run.")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sentinel-stats">
        {[
          { k: "Dernier passage", v: fmtAgo(data?.seconds_since_last_run), sub: fmtShort(data?.last_run_at) },
          { k: "Disponibilité", v: stats.uptime_pct != null ? `${stats.uptime_pct} %` : "—", sub: `${stats.ok || 0}/${stats.window || 0} passages${stats.skipped ? ` · ${stats.skipped} ignoré(s)` : ""}` },
          { k: "Latence médiane", v: stats.p50_ms != null ? `${stats.p50_ms} ms` : "—", sub: stats.p95_ms != null ? `p95 ${stats.p95_ms} ms` : "" },
          { k: "Dernier succès", v: fmtShort(data?.last_success_at), sub: data?.last_failure_at ? `Dernier échec ${fmtShort(data.last_failure_at)}` : "aucun échec" },
        ].map((c) => (
          <div key={c.k} className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-zinc-500">{c.k}</div>
            <div className="text-lg font-bold text-zinc-900 mt-1">{c.v}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h2 className="text-base font-semibold text-zinc-900">{tr("Dernier parcours — détail des étapes", "Last run — step by step")}</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          Cible <code>{data?.target || "—"}</code> · compte <code>{data?.sentinel_email || "—"}</code>
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="sentinel-steps-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">{tr("État", "State")}</th>
                <th className="py-2 pr-3">{tr("Étape", "Step")}</th>
                <th className="py-2 pr-3 text-right">HTTP</th>
                <th className="py-2 pr-3 text-right">Latence</th>
                <th className="py-2">{tr("Détail", "Detail")}</th>
              </tr>
            </thead>
            <tbody>
              {(lastRun?.steps || []).map((s) => (
                <tr key={s.name} className="border-b border-zinc-100" data-testid={`sentinel-step-${s.name}`}>
                  <td className="py-2 pr-3">{s.ok ? "✅" : "❌"}</td>
                  <td className="py-2 pr-3 text-zinc-800">{s.label}</td>
                  <td className="py-2 pr-3 text-right font-mono text-zinc-600">{s.status ?? "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono text-zinc-600">{s.latency_ms} ms</td>
                  <td className="py-2 text-red-700 text-xs">{s.detail || ""}</td>
                </tr>
              ))}
              {!lastRun && (
                <tr><td colSpan={5} className="py-6 text-center text-zinc-500">{tr("Aucun passage enregistré pour l'instant.", "No run recorded yet.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h2 className="text-base font-semibold text-zinc-900">{tr("Latence des derniers passages", "Latency of the last runs")}</h2>
        <p className="text-xs text-zinc-500 mt-0.5 mb-4">
          {tr("Chaque barre est un parcours complet. Les barres rouges sont des échecs.", "Each bar is one complete run. Red bars are failures.")}
        </p>
        <LatencySparkline runs={data?.runs || []} />
        {Object.keys(stats.failures_by_step || {}).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="sentinel-failures-by-step">
            {Object.entries(stats.failures_by_step).map(([step, count]) => (
              <span key={step} className="px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-800 text-xs font-medium">
                {step} · {count}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* B1064 — Tendance de latence sur 30 jours. */}
      <LatencyTrendChart trend={trend} />

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h2 className="text-base font-semibold text-zinc-900">Historique</h2>
        <div className="mt-3 overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm" data-testid="sentinel-history-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">{tr("Quand", "When")}</th>
                <th className="py-2 pr-3">{tr("Résultat", "Result")}</th>
                <th className="py-2 pr-3 text-right">{tr("Durée", "Duration")}</th>
                <th className="py-2 pr-3">{tr("Déclencheur", "Trigger")}</th>
                <th className="py-2">{tr("Étape en échec", "Failed step")}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.runs || []).map((r) => (
                <tr key={r.id} className="border-b border-zinc-100">
                  <td className="py-1.5 pr-3 text-zinc-600 whitespace-nowrap">{fmtTime(r.started_at)}</td>
                  <td className="py-1.5 pr-3">{r.skipped ? "⏸ Ignoré" : r.ok ? "✅ OK" : "❌ Échec"}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-zinc-600">{r.total_ms} ms</td>
                  <td className="py-1.5 pr-3 text-zinc-500 text-xs">{r.trigger}</td>
                  <td className="py-1.5 text-red-700 text-xs">
                    {r.failed_step || (r.skipped ? <span className="text-zinc-500">{tr("rate-limiter — non compté", "rate limiter — not counted")}</span> : "")}
                  </td>
                </tr>
              ))}
              {!(data?.runs || []).length && (
                <tr><td colSpan={5} className="py-6 text-center text-zinc-500">{tr("Aucun historique.", "No history.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Tâche 1207 (Eric 2026-06-26) — Sentinelle Clés IA. Placée juste après
          la sentinelle de connexion : ce sont les deux pannes qui rendent
          Knoria inutilisable sans qu'aucune page ne soit « en erreur ». */}
      <AIKeysSentinelCard initial={bootstrap?.ai_keys} initialWeekly={bootstrap?.ai_keys_weekly} awaiting={bootstrapPending} />
      {/* Lot F — une clé BYOK illisible retombe silencieusement sur la clé Knoria. */}
      <MasterKeyHealthCard initial={bootstrap?.master_key} awaiting={bootstrapPending} />
      {/* Lot D — la diarisation est restée coupée un mois sans rien afficher. */}
      <KillSwitchesCard initial={bootstrap?.kill_switches} awaiting={bootstrapPending} />
      {/* Tache 1291 — francais des titres generes par IA. */}
      <AiTitlesSentinelCard />

      {/* Bogue 1202 (Eric 2026-06-26) — Les erreurs Cloudflare ne laissent
          aucune trace serveur (l'origine n'a pas répondu) et les logs du
          conteneur déployé ne sont pas consultables : on affiche ce que le
          navigateur a constaté. */}
      <GatewayErrorsPanel initial={bootstrap?.gateway_errors} awaiting={bootstrapPending} />

      {/* Tâche 1216 — Le capteur qui manquait : /tmp saturé était la cause
          commune des bogues 1213 (morceau audio introuvable) et 1215. */}
      <DiskSentinelCard initial={bootstrap?.disk} awaiting={bootstrapPending} />

      {/* Tâche 1246 — Le scan des embeddings ne dépendait que d'un
          planificateur externe à brancher à la main. */}
      <EmbeddingScanCard initial={bootstrap?.embeddings} awaiting={bootstrapPending} />

      {/* Tâches 1247/1248 — Les tâches quotidiennes tournent enfin dans le
          pod, au lieu d'attendre un planificateur externe jamais branché. */}
      <DailyJobsCard initial={bootstrap?.daily_jobs} awaiting={bootstrapPending} />

      {/* Tâche 1257 — Un paiement encaissé dont l'abonnement ne s'ouvre pas ne
          se voyait nulle part : le webhook avalait tout en silence. */}
      <StripeSentinelCard initial={bootstrap?.stripe} awaiting={bootstrapPending} />

      {/* Tâche 1250 — Le guide du domaine d'envoi doit vivre là où l'alerte
          Resend est lue, avec le test d'envoi à portée de clic. */}
      <ResendSentinelCard />

      {/* Tâche 1224 — Le jeton de session dans l'URL de lecture : combien de
          liens s'en servent encore, et le bouton pour le couper quand ce
          compteur tombe à zéro. */}
      <LegacyStreamTokenCard initial={bootstrap?.legacy_token} awaiting={bootstrapPending} />

      {/* Tâche 1227 — Le pod de test n'envoie plus de courriels par défaut ;
          cet interrupteur les autorise 60 min quand on veut vraiment
          exercer la chaîne Resend. */}
      <TestEmailGateCard initial={bootstrap?.test_emails} awaiting={bootstrapPending} />

      {/* B1054 — Santé proactive : tâches planifiées + erreurs serveur.
          Ces deux surveillances partagent la politique d'alerte de la
          Sentinelle (Eric + Vincent, 1er échec puis rappel horaire). */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="monitor-crons">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-zinc-900">{tr("Tâches planifiées", "Scheduled tasks")}</h2>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">
              Sur les 7 tâches <code>/api/cron/*</code>, une seule enregistrait son passage :
              les 6 autres étaient invisibles. Chacune est désormais instrumentée. Une tâche
              marquée « jamais vue » n'a encore aucune exécution enregistrée — elle n'est
              probablement pas branchée au planificateur externe, donc <strong>aucune alerte
              n'est envoyée</strong>. On n'alerte que sur une tâche qui tournait et s'est arrêtée.
            </p>
          </div>
          <button
            onClick={sendWeeklyReport}
            disabled={sendingReport}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#002FA7] text-[#002FA7] text-sm font-semibold hover:bg-[#002FA7]/5 disabled:opacity-50 shrink-0"
            data-testid="weekly-report-send-btn"
          >
            <Envelope size={16} />
            {sendingReport ? "Envoi…" : "Envoyer le rapport hebdo"}
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="monitor-crons-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">{tr("État", "State")}</th>
                <th className="py-2 pr-3">Tâche</th>
                <th className="py-2 pr-3">Cadence attendue</th>
                <th className="py-2 pr-3">Seuil d'alerte</th>
                <th className="py-2 pr-3">Dernière exécution</th>
                <th className="py-2">{tr("Durée", "Duration")}</th>
              </tr>
            </thead>
            <tbody>
              {(monitor?.crons || []).map((c) => (
                <tr key={c.job} className="border-b border-zinc-100" data-testid={`monitor-cron-${c.job}`}>
                  <td className="py-2 pr-3">{c.overdue ? "❌" : c.never_ran ? "⚪" : "✅"}</td>
                  <td className="py-2 pr-3 text-zinc-800">{c.label}</td>
                  <td className="py-2 pr-3 text-zinc-600">{c.cadence_fr}</td>
                  <td className="py-2 pr-3 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min={1}
                        max={2160}
                        defaultValue={Math.round(c.overdue_after_seconds / 3600)}
                        onBlur={(e) => {
                          const hours = parseInt(e.target.value, 10);
                          const before = Math.round(c.overdue_after_seconds / 3600);
                          if (!Number.isFinite(hours) || hours === before) return;
                          saveSettings({ cron_overrides: { [c.job]: hours * 3600 } });
                        }}
                        className="w-16 rounded border border-zinc-300 px-2 py-1 text-xs text-right"
                        data-testid={`cron-threshold-input-${c.job}`}
                      />
                      <span className="text-zinc-500">h</span>
                      {c.customized && (
                        <button
                          onClick={() => saveSettings({ cron_overrides: { [c.job]: 0 } })}
                          className="text-[#002FA7] underline"
                          title={`Revenir à ${Math.round(c.default_overdue_after_seconds / 3600)} h`}
                          data-testid={`cron-threshold-reset-${c.job}`}
                        >
                          défaut
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-zinc-600">
                    {c.never_ran ? <span className="text-amber-700">jamais vue</span> : fmtShort(c.last_run_at)}
                  </td>
                  <td className="py-2 font-mono text-xs text-zinc-500">
                    {c.last_duration_ms != null ? `${c.last_duration_ms} ms` : "—"}
                  </td>
                </tr>
              ))}
              {!(monitor?.crons || []).length && (
                <tr><td colSpan={6} className="py-6 text-center text-zinc-500">Chargement…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="monitor-errors">
        <h2 className="text-base font-semibold text-zinc-900">Erreurs serveur (7 derniers jours)</h2>
        <p className="text-xs text-zinc-500 mt-0.5 mb-3 max-w-3xl">
          Deux niveaux d'alerte : <strong>courriel immédiat</strong> dès la première erreur sur
          une route critique (connexion, téléversement, paiement), sinon <strong>à partir de 3
          erreurs en 10 minutes</strong>. Ces compteurs sont persistés — avant, ils vivaient en
          mémoire et disparaissaient à chaque redémarrage.
        </p>
        <div className="flex items-center gap-6 mb-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500">Total</div>
            <div className={`text-2xl font-bold ${(monitor?.server_errors?.total || 0) > 0 ? "text-amber-700" : "text-emerald-700"}`}>
              {monitor?.server_errors?.total ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500">Sur route critique</div>
            <div className={`text-2xl font-bold ${(monitor?.server_errors?.critical || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
              {monitor?.server_errors?.critical ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500">Incidents ouverts</div>
            <div className={`text-2xl font-bold ${(monitor?.open_incidents || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
              {monitor?.open_incidents ?? "—"}
            </div>
          </div>
        </div>
        {(monitor?.server_errors?.by_path || []).length === 0 ? (
          <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="monitor-errors-empty">
            Aucune erreur serveur sur la période.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="monitor-errors-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">Route</th>
                <th className="py-2 pr-3 text-right">Erreurs</th>
                <th className="py-2">Criticité</th>
              </tr>
            </thead>
            <tbody>
              {monitor.server_errors.by_path.map((e) => (
                <tr key={e.path} className="border-b border-zinc-100">
                  <td className="py-1.5 pr-3 font-mono text-xs text-zinc-700">{e.path}</td>
                  <td className="py-1.5 pr-3 text-right font-semibold">{e.count}</td>
                  <td className="py-1.5 text-xs">
                    {e.critical ? <span className="text-red-700 font-semibold">route critique</span> : <span className="text-zinc-500">standard</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* B1062 — Audit des familles d'erreurs surveillées. */}
      <MonitoringCoveragePanel coverage={coverage} onRunAudit={runAudit} running={auditing} />

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h2 className="text-base font-semibold text-zinc-900">{tr("Canari de transport navigateur", "Browser transport canary")}</h2>
        <p className="text-xs text-zinc-500 mt-0.5 mb-3 max-w-3xl">
          {tr(
            "Une sonde serveur ne peut pas voir une panne côté navigateur. Le 2 septembre 2026, l'adaptateur axios était cassé : plus aucune requête ne quittait le navigateur alors que le serveur répondait parfaitement. Au chargement de l'app, ce canari compare un même appel en axios et en fetch() natif. Si axios échoue là où fetch réussit, un rapport arrive ici et une alerte part immédiatement.",
            "A server-side probe cannot see a browser-side outage. On 2 September 2026 the axios adapter was broken: no request left the browser at all while the server answered perfectly. On app load this canary compares the same call through axios and through native fetch(). If axios fails where fetch succeeds, a report lands here and an alert goes out immediately.",
          )}
        </p>
        {canaries.length === 0 ? (
          <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="sentinel-canary-empty">
            {tr("Aucune panne de transport signalée. Le transport axios est sain sur tous les navigateurs observés.", "No transport outage reported. The axios transport is healthy on every observed browser.")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sentinel-canary-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-3">{tr("Quand", "When")}</th>
                  <th className="py-2 pr-3">{tr("Erreur axios", "axios error")}</th>
                  <th className="py-2 pr-3">Adapter</th>
                  <th className="py-2 pr-3">Bundle</th>
                  <th className="py-2">{tr("Navigateur", "Browser")}</th>
                </tr>
              </thead>
              <tbody>
                {canaries.map((c, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-1.5 pr-3 text-zinc-600 whitespace-nowrap">{fmtTime(c.detected_at)}</td>
                    <td className="py-1.5 pr-3 text-red-700 font-mono text-xs">{c.axios_error}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-zinc-600">{c.adapter_spec}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-zinc-600">{c.bundle_version}</td>
                    <td className="py-1.5 text-xs text-zinc-500 truncate max-w-[220px]">{c.user_agent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="monitor-settings">
        <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
          <Gear size={18} />
          {tr("Réglages de la surveillance", "Monitoring settings")}
        </h2>
        <p className="text-xs text-zinc-500 mt-0.5 mb-4 max-w-3xl">
          {tr(
            "Réglages propres à l'organisation active : une liste globale obligeait toutes les organisations à partager les mêmes destinataires, donc impossible d'en configurer une nouvelle sans arroser les autres. Seuls les fondateurs et administrateurs internes apparaissent ici — ce sont des alertes techniques, pas des informations à envoyer aux cabinets clients.",
            "Settings specific to the active organisation: a single global list forced every organisation to share the same recipients, so a new one could not be set up without spamming the others. Only founders and internal admins appear here — these are technical alerts, not information to send to client firms.",
          )}
        </p>

        {/* B1075 (Eric 2026-09-05 SPEC STRICK PROD) — Interrupteur d'alertes :
            pendant la configuration d'une nouvelle organisation, on veut
            pouvoir tout mettre en pause sans décocher les destinataires un
            par un. */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-zinc-900">
              {tr("Alertes courriel", "Email alerts")}{" "}
              {settings?.alerts_enabled ? tr("activées", "on") : tr("en pause", "paused")}
            </div>
            <p className="text-xs text-zinc-500 max-w-xl">
              {tr(
                "En pause, aucun courriel ne part pour cette organisation, quel que soit le nombre de destinataires cochés. La surveillance continue et les incidents restent visibles au journal.",
                "While paused, no email leaves for this organisation, however many recipients are ticked. Monitoring carries on and incidents stay visible in the log.",
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => saveSettings({ alerts_enabled: !settings?.alerts_enabled })}
            disabled={savingSettings || !settings}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              settings?.alerts_enabled ? "bg-[#002FA7]" : "bg-zinc-300"
            }`}
            role="switch"
            aria-checked={!!settings?.alerts_enabled}
            data-testid="monitor-alerts-toggle"
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                settings?.alerts_enabled ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        {/* B1075 — Périodicité du rappel et du rapport, propres à l'organisation. */}
        <div className="mb-5 grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {tr("Rappel tant que le problème dure", "Reminder while the problem lasts")}
            </span>
            <select
              value={settings?.reminder_interval_hours ?? 1}
              disabled={savingSettings || !settings}
              onChange={(e) => saveSettings({ reminder_interval_hours: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm"
              data-testid="monitor-reminder-select"
            >
              {(settings?.allowed_reminder_hours || [0, 1, 2, 6, 12, 24, 48]).map((h) => (
                <option key={h} value={h}>
                  {/* B1110 (Eric 2026-09-08) — « Aucun rappel » (0 h) : une
                      seule alerte à l'apparition du problème. */}
                  {h === 0
                    ? tr("Aucun rappel", "No reminder")
                    : h === 1
                      ? tr("Chaque heure", "Every hour")
                      : tr(`Aux ${h} h`, `Every ${h} h`)}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-zinc-500">
              {tr(
                "La cadence retenue est la plus courte parmi les organisations actives : une organisation qui demande un rappel horaire ne doit pas attendre à cause d'une autre.",
                "The pace applied is the shortest among active organisations: one that asks for an hourly reminder must not wait because of another.",
              )}
            </span>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {tr("Rapport régulier de fiabilité", "Regular reliability report")}
            </span>
            <select
              value={settings?.report_frequency ?? "weekly"}
              disabled={savingSettings || !settings}
              onChange={(e) => saveSettings({ report_frequency: e.target.value })}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm"
              data-testid="monitor-report-frequency-select"
            >
              <option value="daily">{tr("Chaque jour", "Every day")}</option>
              <option value="weekly">{tr("Chaque semaine (lundi 7 h)", "Every week (Monday 7 a.m.)")}</option>
              <option value="monthly">{tr("Chaque mois (le 1er)", "Every month (the 1st)")}</option>
            </select>
            <span className="text-[11px] text-zinc-500">
              {tr("Envoyé le matin, heure de Montréal, aux destinataires cochés ci-dessous.", "Sent in the morning, Montreal time, to the recipients ticked below.")}
            </span>
          </label>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
              Qui reçoit les alertes et le rapport (cette organisation)
            </div>
            {/* B1093 puis B1101 — Sans choix enregistré, sont PRÉ-COCHÉS les
                fondateurs de Knoria et les administrateurs TECHNIQUES de
                l'organisation ; les dirigeants métier (CEO, président, VP)
                restent proposés mais décochés. Les alertes démarrent en pause. */}
            {settings?.recipients_are_default && (
              <p
                className="text-[11px] text-zinc-500 mb-2 max-w-md"
                data-testid="monitor-recipients-default"
              >
                {tr(
                  "Liste par défaut : les fondateurs de Knoria et les administrateurs techniques de cette organisation. Les dirigeants métier (CEO, président, VP) sont proposés plus bas mais décochés — une alerte de latence n'est pas leur sujet. Tant que les alertes sont en pause, aucun courriel ne part — même à ces adresses.",
                  "Default list: Knoria founders and this organisation's technical admins. Business leaders (CEO, president, VP) are offered further down but unticked — a latency alert is not their subject. While alerts are paused, no email goes out — not even to these addresses.",
                )}
              </p>
            )}
            <div className="space-y-2" data-testid="monitor-recipients">
              {(settings?.candidates || []).map((c) => (
                <label
                  key={c.email}
                  className="flex items-start gap-2.5 cursor-pointer group"
                  data-testid={`recipient-row-${c.email}`}
                >
                  <input
                    type="checkbox"
                    checked={c.selected}
                    disabled={savingSettings}
                    onChange={() => toggleRecipient(c.email)}
                    className="mt-1 h-4 w-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]/30"
                    data-testid={`recipient-checkbox-${c.email}`}
                  />
                  <span>
                    <span className="text-sm font-medium text-zinc-900 group-hover:text-[#002FA7]">
                      {c.name}
                    </span>
                    {c.is_founder && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#002FA7]/10 text-[#002FA7]">
                        fondateur
                      </span>
                    )}
                    <span className="block text-xs text-zinc-500">
                      {c.display_role} · {c.email}
                    </span>
                  </span>
                  {/* B1111 (Eric 2026-09-08) — On pouvait ajouter une adresse
                      mais pas la retirer : une saisie erronée restait à
                      l'écran pour toujours. Seules les adresses ajoutées à la
                      main sont supprimables ; un fondateur ou un admin
                      technique se décoche, il ne s'efface pas. */}
                  {c.removable && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); removeRecipient(c.email); }}
                      disabled={savingSettings}
                      className="ml-auto mt-0.5 text-xs font-semibold text-zinc-400 hover:text-red-700 disabled:opacity-40"
                      data-testid={`recipient-remove-${c.email}`}
                      title={`Retirer définitivement ${c.email} de la liste`}
                      aria-label={`Retirer ${c.email}`}
                    >
                      Retirer
                    </button>
                  )}
                </label>
              ))}
              {!(settings?.candidates || []).length && (
                <p className="text-sm text-zinc-500">Chargement…</p>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
              {tr("Alerte de lenteur", "Slowness alert")}
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              {tr(
                "Une latence installée précède presque toujours une panne. Un pic isolé, non : on n'alerte donc qu'après plusieurs parcours lents d'affilée.",
                "Sustained latency almost always precedes an outage. An isolated spike does not: we therefore only alert after several slow runs in a row.",
              )}
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <label className="block">
                <span className="block text-xs text-zinc-600 mb-1">{tr("Seuil (millisecondes)", "Threshold (milliseconds)")}</span>
                <input
                  type="number"
                  min={500}
                  max={60000}
                  step={100}
                  defaultValue={settings?.slow_threshold_ms ?? 3000}
                  key={`thr-${settings?.slow_threshold_ms}`}
                  onBlur={(e) => {
                    const ms = parseInt(e.target.value, 10);
                    if (!Number.isFinite(ms) || ms === settings?.slow_threshold_ms) return;
                    saveSettings({ slow_threshold_ms: ms });
                  }}
                  className="w-28 rounded-lg border border-zinc-300 px-3 py-2 text-sm"
                  data-testid="slow-threshold-input"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-zinc-600 mb-1">{tr("Parcours lents d'affilée", "Slow runs in a row")}</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  defaultValue={settings?.slow_consecutive ?? 3}
                  key={`cons-${settings?.slow_consecutive}`}
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (!Number.isFinite(n) || n === settings?.slow_consecutive) return;
                    saveSettings({ slow_consecutive: n });
                  }}
                  className="w-24 rounded-lg border border-zinc-300 px-3 py-2 text-sm"
                  data-testid="slow-consecutive-input"
                />
              </label>
            </div>
            <p className="text-xs text-zinc-500 mt-3" data-testid="slow-rule-summary">
              {tr(
                `Règle actuelle : alerte si ${settings?.slow_consecutive ?? 3} parcours consécutifs dépassent ${((settings?.slow_threshold_ms ?? 3000) / 1000).toFixed(1)} s. Les valeurs hors bornes sont automatiquement ramenées à un intervalle sûr.`,
                `Current rule: alert if ${settings?.slow_consecutive ?? 3} consecutive runs exceed ${((settings?.slow_threshold_ms ?? 3000) / 1000).toFixed(1)} s. Out-of-range values are automatically pulled back to a safe interval.`,
              )}
            </p>
            {settings?.updated_at && (
              <p className="text-xs text-zinc-400 mt-2">
                {tr("Dernière modification :", "Last change:")} {fmtShort(settings.updated_at)}
                {settings.updated_by ? ` par ${settings.updated_by}` : ""}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <ReportPreviewModal
            endpoint="/api/reliability/weekly-report/preview"
            testid="monitor-report-preview"
            label="Aperçu du rapport de fiabilité"
          />
        </div>

        <PipelineDurationsPanel />

        <RouteLatencyPanel initial={bootstrap?.route_latency} awaiting={bootstrapPending} />

        <ReportSendLog
          endpoint="/api/health-monitor/report-log"
          testid="monitor-report-log"
        />
      </section>

      {/* Tâche 1143 (Eric 2026-06-16) — Les huit familles de courriels
          automatiques qui n'obéissaient à aucun réglage. */}
      <SystemNotificationsCard />

      <p className="text-xs text-zinc-500" data-testid="sentinel-alert-config">
        Alertes courriel : <strong>{data?.alerts_enabled ? "activées" : "désactivées (environnement preview)"}</strong>
        {" · "}Destinataires : {(settings?.effective_recipients || data?.alert_recipients || []).join(", ") || "—"}
        {" · "}Rappel toutes les {Math.round((data?.reminder_interval_seconds || 3600) / 60)} minutes tant que la panne persiste.
        {" · "}Rapport hebdo : lundi {monitor?.weekly_report_hour_local ?? 7} h, heure de Montréal.
      </p>
    </div>
  );
}
